import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Compass, Sparkles, ShieldCheck, ArrowLeft, LogOut, FileText, ChevronRight, GraduationCap, Award, Home,
} from 'lucide-react';
import { isAdminAuthenticated, logoutAdmin } from '@/lib/adminAuth';
import { getAdminBase, realmForBase } from '@/lib/adminBase';
import { brandForRealm, isWhiteLabel, type AdminBrand } from '@/lib/adminBrand';
import BrandStudio from '@/components/zera/BrandStudio';
import CrmSection from '@/components/admin/CrmSection';
import AdminSuite from '@/components/admin/AdminSuite';
import BrandGuidelines from '@/components/admin/BrandGuidelines';
import { GlassPanel, SectionTitle } from '@/components/admin/ui';

type View = 'home' | 'crm' | 'social' | 'admin';

const sectionsForBrand = (brand: AdminBrand) => [
  {
    key: 'crm' as View,
    title: brand.crmTitle,
    desc: brand.crmDesc,
    icon: isWhiteLabel(brand.realm) ? Home : Compass,
    accent: 'from-[#1A3B5C]/40 to-transparent',
  },
  {
    key: 'social' as View,
    title: brand.socialTitle,
    desc: brand.socialDesc,
    icon: Sparkles,
    accent: 'from-[#C9A961]/30 to-transparent',
  },
  {
    key: 'admin' as View,
    title: brand.adminTitle,
    desc: brand.adminDesc,
    icon: ShieldCheck,
    accent: 'from-emerald-500/25 to-transparent',
  },
];

const AdminHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initial = (params.get('view') as View) || 'home';
  const [view, setView] = useState<View>(['home', 'crm', 'social', 'admin'].includes(initial) ? initial : 'home');

  const base = getAdminBase(location.pathname);
  const realm = realmForBase(base);
  const brand = brandForRealm(realm);
  const SECTIONS = sectionsForBrand(brand);
  const LogoIcon = isWhiteLabel(realm) ? Home : Compass;

  useEffect(() => {
    document.title = brand.portalName;
    if (!isAdminAuthenticated(realm)) navigate(base, { replace: true });
  }, [navigate, base, realm, brand.portalName]);

  const handleLogout = () => {
    logoutAdmin();
    navigate(base, { replace: true });
  };

  const current = SECTIONS.find((s) => s.key === view);


  return (
    <div className="min-h-screen bg-black text-white">
      {/* ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-[#C9A961]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-[#1A3B5C]/20 blur-[140px]" />
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <button onClick={() => setView('home')} className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04]">
              <LogoIcon className="h-4 w-4 text-[#C9A961]" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium tracking-tight text-white">{brand.portalName}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{getAdminBase(location.pathname).replace('/', '')}</div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            {view !== 'home' && (
              <button
                onClick={() => setView('home')}
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:text-white"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Hub
              </button>
            )}
            <button
              onClick={() => navigate(`${getAdminBase(location.pathname)}/content-planner`)}
              className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:text-white sm:flex"
            >
              <FileText className="h-3.5 w-3.5" /> Content Planner
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:border-red-400/40 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1400px] px-6 py-12 lg:px-10">
        {view === 'home' && (
          <>
            <div className="mb-14 text-center">
              <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.4em] text-[#C9A961]">
                Operations Console
              </div>
              <h1 className="text-4xl font-light tracking-tight text-white sm:text-6xl">
                {brand.welcomeLead} <span className="text-[#E4C77F]">{brand.welcomeAccent}</span>
              </h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/50">
                {brand.welcomeSub}
              </p>
            </div>


            <div className="grid gap-6 md:grid-cols-3">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => setView(s.key)}
                    className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-left transition-all duration-500 hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.06]"
                  >
                    <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} />
                    <div className="relative">
                      <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.05]">
                        <Icon className="h-6 w-6 text-[#E4C77F]" />
                      </div>
                      <h3 className="text-xl font-medium text-white">{s.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/50">{s.desc}</p>
                      <div className="mt-6 flex items-center gap-1.5 text-xs font-medium text-white/60 transition-colors group-hover:text-white">
                        Open section <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Social Media Hub Academy — interactive training + certification */}
            <button
              onClick={() => navigate(`${base}/training`)}
              className="group relative mt-6 block w-full overflow-hidden rounded-3xl border border-[#C9A961]/30 bg-gradient-to-r from-[#C9A961]/[0.12] via-transparent to-[#1A3B5C]/20 p-6 text-left transition-all duration-500 hover:-translate-y-0.5 hover:border-[#C9A961]/60 sm:p-8"
            >
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#C9A961]/40 bg-[#C9A961]/15">
                    <GraduationCap className="h-6 w-6 text-[#E4C77F]" />
                  </div>
                  <div>
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.35em] text-[#C9A961]">
                      New · Interactive training
                    </div>
                    <h3 className="text-xl font-medium text-white">{brand.academyName}</h3>

                    <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-white/50">
                      A Complete Rundown of the Social Media Hub
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-start rounded-full bg-[#C9A961] px-5 py-2.5 text-xs font-semibold text-black shadow-lg shadow-[#C9A961]/25 transition-all group-hover:bg-[#E4C77F] sm:self-center">
                  <Award className="h-4 w-4" /> Open Academy
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </button>
          </>
        )}

        {view !== 'home' && current && (
          <SectionTitle eyebrow={current.title} title={current.title} subtitle={current.desc} />
        )}

        {view === 'crm' && <CrmSection />}

        {view === 'social' && (
          <div className="space-y-10">
            <GlassPanel className="overflow-hidden">
              <BrandStudio />
            </GlassPanel>
            <BrandGuidelines />
          </div>
        )}


        {view === 'admin' && <AdminSuite />}
      </main>
    </div>
  );
};

export default AdminHub;
